import { Alert, Button, Spinner } from "flowbite-react";
import Input from "../inputs/Input";
import PhoneInput from "../inputs/PhoneInput";
import CPFInput from "../inputs/CPFInput";
import CNPJInput from "../inputs/CNPJInput";
import SelectInput from "../inputs/SelectInput";

import { Controller, useForm } from "react-hook-form";
import { useEffect, useState, useRef } from "react";
import { useQueryClient } from "react-query";
import axios from "../../config/axiosInstance";
import { ADMIN, USERS } from "../../config/apiConfig";
import useCEP from "../../hooks/useCEP";
import { isValidCNPJ, isValidCPF } from "../../utils/utils";
import { FaCheckCircle, FaRegQuestionCircle } from "react-icons/fa";
import moment from "moment";
import { useApplication } from "../../contexts/ApplicationContext";
import { Tooltip } from "react-tooltip";
import { useNavigate } from "react-router-dom";
import AppBreadcrumb from "../common/AppBreadcrumb";

const mergeHandlers =
  (...fns) =>
  (e) =>
    fns.filter(Boolean).forEach((fn) => fn(e));

const WIZARD_STEPS = [
  { label: "Revendedor", subtitle: "Dados cadastrais" },
  { label: "Usuário", subtitle: "Acesso inicial" },
  { label: "Confirmação", subtitle: "Revisão final" },
];

const StepHeader = ({ activeStep, steps = WIZARD_STEPS }) => {
  const safeStep = Math.min(Math.max(activeStep, 1), steps.length);
  return (
    <ol className="grid grid-cols-2 gap-1.5 md:grid-cols-3 md:gap-2 mb-6">
      {steps.map((item, idx) => {
        const step = idx + 1;
        const done = safeStep > step;
        const current = safeStep === step;
        return (
          <li
            key={item.label}
            className={[
              "rounded-lg border p-2 transition-all",
              current
                ? "border-blue-300 bg-white shadow-sm"
                : done
                  ? "border-blue-200 bg-blue-50/60"
                  : "border-gray-200 bg-white/70",
            ].join(" ")}
          >
            <div className="flex items-start gap-2">
              <span
                className={[
                  "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-[10px] font-bold",
                  current
                    ? "border-blue-500 bg-blue-500 text-white"
                    : done
                      ? "border-blue-400 bg-blue-100 text-blue-700"
                      : "border-gray-300 bg-gray-100 text-gray-500",
                ].join(" ")}
              >
                {done ? "\u2713" : step}
              </span>
              <div className="min-w-0">
                <div
                  className={[
                    "truncate text-xs font-semibold",
                    current
                      ? "text-blue-700"
                      : done
                        ? "text-blue-800"
                        : "text-gray-600",
                  ].join(" ")}
                >
                  {item.label}
                </div>
                <div className="hidden truncate text-[10px] text-gray-500 md:block">
                  {item.subtitle}
                </div>
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
};

const SectionCard = ({ title, subtitle, children, right }) => (
  <div className="rounded-2xl border bg-white shadow-sm p-4">
    <div className="flex items-start justify-between gap-4 mb-4">
      <div>
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
      {right}
    </div>
    <div className="space-y-4">{children}</div>
  </div>
);

const FormRow = ({ children, className = "" }) => (
  <div className={`grid grid-cols-1 gap-4 ${className}`}>{children}</div>
);

const ReviewItem = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 py-2 border-b last:border-b-0">
    <span className="text-sm text-gray-500">{label}</span>
    <span className="text-sm font-medium text-gray-900 text-right break-all">
      {value || "—"}
    </span>
  </div>
);

const AdminRepresentativeForm = () => {
  const navigate = useNavigate();
  const { userPermission, user } = useApplication();
  const queryClient = useQueryClient();
  const [activeStep, setActiveStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorQuery, setErrorQuery] = useState(false);

  const today = moment();
  const creationDay = today.clone().subtract(1, "day").date();
  const expirationDay = today.clone().subtract(1, "day").add(10, "days").date();
  const formatDay = (d) => d.toString().padStart(2, "0");

  const defaultValues = {
    company: {
      typeCustomer: "CPF",
      typePayment: "POS",
      generateAutomaticPayment: 1,
      paymentCreationDay: formatDay(creationDay),
      paymentExpirationDay: formatDay(expirationDay),
      priceSurvey: "10",
      priceCV: "10",
    },
    representative: {
      representativeUrl: "",
      accessToken: "",
    },
  };

  const {
    register,
    getValues,
    handleSubmit,
    setValue,
    setError,
    watch,
    control,
    formState: { errors },
    clearErrors,
    reset,
  } = useForm({ defaultValues, shouldUnregister: false });

  const { setCep, address, loading: loadingCEP } = useCEP();

  // Data

  // CEP autofill
  useEffect(() => {
    setValue("company.address", address?.logradouro);
    setValue("company.addressNeighborhood", address?.bairro);
    setValue("company.addressCity", address?.localidade);
  }, [address, setValue]);

  const onBlurSearchCEP = () => {
    setCep(getValues("company.cep"));
  };

  // Dedupe/validações
  const onBlurInfo = async (e, type) => {
    try {
      await axios.get(
        `${ADMIN.GET_COMPANY_BY_INFO}/${e.target.value}?type=${type}`,
      );
      setError(`${e.target.name}`, {
        type: "manual",
        message: "Já possui este campo cadastrado!",
      });
    } catch {
      clearErrors(e.target.name);
    }
  };

  const onBlurEmailInfo = async (e) => {
    try {
      await axios.get(`${USERS.GET_USER_BY_EMAIL}/${e.target.value}`);
      clearErrors(e.target.name);
    } catch {
      setError(`${e.target.name}`, {
        type: "manual",
        message: "Já possui este campo cadastrado!",
      });
    }
  };

  // Validação CPF/CNPJ
  const checkDocument = (e, type) => {
    const unmasked = e.target.value.replace(/\D/g, "");
    const isValid =
      unmasked.length === 11
        ? isValidCPF(unmasked)
        : unmasked.length === 14
          ? isValidCNPJ(unmasked)
          : false;
    if (!isValid) {
      setError(`company.${type}`, {
        type: "manual",
        message: "Documento inválido",
      });
    } else {
      clearErrors(`company.${type}`);
      onBlurInfo(e, type);
    }
  };

  // Ações de etapas
  const handleStep1Submit = () => setActiveStep(2);
  const handleStep2Submit = () => setActiveStep(3);

  const handleFinishSubmit = async (data) => {
    setIsSubmitting(true);
    setErrorQuery(false);
    try {
      data.queries = [];

      await axios.post(ADMIN.NEW_REPRESENTATIVE, data);
      queryClient.invalidateQueries(["listRepresentatives"], { exact: false });
      queryClient.refetchQueries(["listRepresentatives"], {
        active: true,
        inactive: true,
      });
      setActiveStep(4);
    } catch (error) {
      setErrorQuery(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const userEdited = useRef({
    address: false,
    addressNeighborhood: false,
    addressCity: false,
  });

  // marcar como editado via register.onChange (endereços)
  const regAddress = register("company.address", {
    onChange: () => (userEdited.current.address = true),
  });
  const regNeighborhood = register("company.addressNeighborhood", {
    onChange: () => (userEdited.current.addressNeighborhood = true),
  });
  const regCity = register("company.addressCity", {
    onChange: () => (userEdited.current.addressCity = true),
  });

  useEffect(() => {
    if (!address) return;
    const { logradouro, bairro, localidade } = address || {};

    if (logradouro && !userEdited.current.address) {
      setValue("company.address", logradouro, {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
    if (bairro && !userEdited.current.addressNeighborhood) {
      setValue("company.addressNeighborhood", bairro, {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
    if (localidade && !userEdited.current.addressCity) {
      setValue("company.addressCity", localidade, {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
  }, [address, setValue]);

  // Derivados de UI
  const isCPF = watch("company.typeCustomer") === "CPF";
  const powerUser = userPermission === "POWER_USER";

  return (
    <div>
      <AppBreadcrumb
        items={[{ label: "Representantes", to: "/admin/representatives" }]}
        currentLabel="Novo representante"
        className="mb-4"
      />
      <div className="bg-white rounded-xl p-2 md:p-5 space-y-6">
        <StepHeader activeStep={activeStep} />

        {/* STEP 1 */}
        {activeStep === 1 && (
          <form
            onSubmit={handleSubmit(handleStep1Submit)}
            className="space-y-6"
          >
            <SectionCard
              title="Dados do revendedor"
              subtitle="Preencha as informações cadastrais e de contato."
              right={
                loadingCEP ? (
                  <span className="inline-flex items-center gap-2 text-xs text-gray-500">
                    <Spinner size="xs" /> Buscando endereço pelo CEP…
                  </span>
                ) : null
              }
            >
              <FormRow className="lg:grid-cols-3">
                <SelectInput
                  register={register}
                  label="Tipo de revendedor"
                  name="company.typeCustomer"
                >
                  <option value="CPF">Pessoa Física</option>
                  <option value="CNPJ">Pessoa Jurídica</option>
                </SelectInput>

                {isCPF ? (
                  <>
                    <div>
                      <Input
                        type="text"
                        register={register}
                        name="company.name"
                        label="Nome completo"
                        required
                      />
                    </div>
                    <div>
                      <Controller
                        name="company.document"
                        control={control}
                        defaultValue=""
                        rules={{ required: "Este campo é obrigatório" }}
                        render={({ field, fieldState }) => (
                          <>
                            <CPFInput
                              name="company.document"
                              label="CPF"
                              required
                              {...field}
                              onBlur={mergeHandlers(field.onBlur, (e) =>
                                checkDocument(e, "document"),
                              )}
                              error={fieldState.error?.message}
                            />
                          </>
                        )}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <Input
                        type="text"
                        register={register}
                        name="company.socialReason"
                        label="Razão Social"
                        required
                      />
                    </div>
                    <div>
                      <Controller
                        name="company.document"
                        control={control}
                        defaultValue=""
                        rules={{ required: "Este campo é obrigatório" }}
                        render={({ field, fieldState }) => (
                          <>
                            <CNPJInput
                              name="company.document"
                              label="CNPJ"
                              required
                              {...field}
                              onBlur={mergeHandlers(field.onBlur, (e) =>
                                checkDocument(e, "document"),
                              )}
                              error={fieldState.error?.message}
                            />
                          </>
                        )}
                      />
                    </div>
                  </>
                )}
              </FormRow>

              {!isCPF && (
                <FormRow className="lg:grid-cols-3">
                  <div>
                    <Input
                      type="text"
                      register={register}
                      name="company.responsibleName"
                      label="Nome do responsável"
                      required
                    />
                  </div>
                  <div>
                    <Controller
                      name="company.responsibleCPF"
                      control={control}
                      defaultValue=""
                      rules={{ required: "Este campo é obrigatório" }}
                      render={({ field, fieldState }) => (
                        <>
                          <CPFInput
                            name="company.responsibleCPF"
                            label="CPF do responsável"
                            required
                            {...field}
                            onBlur={mergeHandlers(field.onBlur, (e) =>
                              checkDocument(e, "responsibleCPF"),
                            )}
                            error={fieldState.error?.message}
                          />
                        </>
                      )}
                    />
                  </div>
                  <SelectInput
                    register={register}
                    label="Cargo"
                    name="company.position"
                  >
                    <option value="SOCIO">Sócio/Administrador</option>
                    <option value="GERENTE">Gerente</option>
                    <option value="ADM">Administrador</option>
                  </SelectInput>
                </FormRow>
              )}

              <FormRow className="lg:grid-cols-3">
                <div>
                  <Input
                    type="email"
                    register={register}
                    name="company.email"
                    label="E-mail"
                    required
                    onBlur={(e) => onBlurInfo(e, "email")}
                  />
                  {errors.company?.email && (
                    <small className="text-xs text-red-500">
                      {errors.company.email.message}
                    </small>
                  )}
                </div>

                <div>
                  <Controller
                    name="company.phoneNumber"
                    control={control}
                    defaultValue=""
                    rules={{ required: "Este campo é obrigatório" }}
                    render={({ field, fieldState }) => (
                      <>
                        <PhoneInput
                          name="company.phoneNumber"
                          label="Celular"
                          required
                          {...field}
                          error={fieldState.error?.message}
                        />
                      </>
                    )}
                  />
                </div>

                <SelectInput
                  register={register}
                  label="Tipo de pagamento"
                  name="company.typePayment"
                  disabled
                >
                  <option value="POS">Pós-pago</option>
                </SelectInput>

                {!isCPF && (
                  <div>
                    <Input
                      type="email"
                      register={register}
                      name="company.financeEmail"
                      label="E-mail do financeiro"
                      required
                      autoComplete="off"
                      onBlur={(e) => onBlurInfo(e, "financeEmail")}
                    />
                    {errors.company?.financeEmail && (
                      <small className="text-xs text-red-500">
                        {errors.company.financeEmail.message}
                      </small>
                    )}
                    <div className="text-xs text-gray-500 italic mt-1">
                      Envio da cobrança para este e-mail, caso haja.
                    </div>
                  </div>
                )}
              </FormRow>

              <div>
                <div className="font-semibold pt-3">Endereço do revendedor</div>
                <div className="text-gray-500 text-sm mt-1">
                  Preencha o endereço do revendedor
                </div>
              </div>

              <FormRow className="lg:grid-cols-3">
                <Input
                  register={register}
                  name="company.cep"
                  label="CEP"
                  required
                  onBlur={onBlurSearchCEP}
                />
                <Input
                  register={register}
                  name="company.address"
                  onChange={() => (userEdited.current.address = true)}
                  label="Endereço"
                  required
                />
                <Input
                  type="number"
                  register={register}
                  name="company.addressNumber"
                  label="Número"
                  required
                />
              </FormRow>

              <FormRow className="lg:grid-cols-3">
                <Input
                  register={register}
                  name="company.addressComplement"
                  label="Complemento"
                />
                <Input
                  register={register}
                  onChange={() =>
                    (userEdited.current.addressNeighborhood = true)
                  }
                  name="company.addressNeighborhood"
                  label="Bairro"
                  required
                />
                <Input
                  register={register}
                  onChange={() => (userEdited.current.addressCity = true)}
                  name="company.addressCity"
                  label="Cidade"
                  required
                />
              </FormRow>
            </SectionCard>

            {powerUser && (
              <SectionCard
                title="White-label (opcional)"
                subtitle="Vincule uma URL e um token do Asaas para este revendedor."
              >
                <FormRow className="lg:grid-cols-2">
                  <Input
                    register={register}
                    name="representative.representativeUrl"
                    label="URL da White-label"
                  />
                  <Input
                    register={register}
                    name="representative.accessToken"
                    label="Token Asaas"
                  />
                  <div className="flex items-end"></div>
                </FormRow>
              </SectionCard>
            )}
            <div className="flex justify-center">
              <Button type="submit" className="shadow md:max-w-60 px-10 ">
                Próximo
              </Button>
            </div>
          </form>
        )}

        {/* STEP 2 */}
        {activeStep === 2 && (
          <form
            onSubmit={handleSubmit(handleStep2Submit)}
            className="space-y-6"
          >
            <SectionCard
              title="Usuário administrador"
              subtitle="Crie o primeiro usuário para acessar o painel."
            >
              <div className="flex justify-end gap-2 items-center  mb-4">
                <Button
                  size="xs"
                  type="button"
                  className="w-fit self-end"
                  onClick={() => {
                    const email = watch("company.email");
                    const name =
                      watch("company.responsibleName") || watch("company.name");
                    const phone = watch("company.phoneNumber");

                    if (email) setValue("customer.email", email);
                    if (name) setValue("customer.name", name);
                    if (phone) setValue("customer.phoneNumber", phone);
                  }}
                >
                  Reutilizar dados preenchidos do representante
                </Button>
                <div>
                  <Tooltip
                    id="tooltip-link"
                    content={
                      <div className="max-w-md relative">
                        Esse botão preenche automaticamente os campos abaixo com
                        o e-mail, nome e celular informados na etapa anterior.
                      </div>
                    }
                  />
                  <FaRegQuestionCircle
                    data-tooltip-id="tooltip-link"
                    className="text-gray-500"
                  />
                </div>
              </div>
              {/* linha 1: nome + e-mail */}
              <FormRow className="lg:grid-cols-2">
                <Input
                  register={register}
                  name="customer.name"
                  label="Nome"
                  required
                />
                <div>
                  <Input
                    type="email"
                    register={register}
                    name="customer.email"
                    label="E-mail"
                    required
                    onBlur={onBlurEmailInfo}
                  />
                  {errors.customer?.email && (
                    <small className="text-xs text-red-500">
                      {errors.customer.email.message}
                    </small>
                  )}
                </div>
              </FormRow>

              {/* linha 2: celular + senha */}
              <FormRow className="md:grid-cols-2">
                <div>
                  <Controller
                    name="customer.phoneNumber"
                    control={control}
                    defaultValue=""
                    rules={{ required: "Este campo é obrigatório" }}
                    render={({ field, fieldState }) => (
                      <>
                        <PhoneInput
                          name="customer.phoneNumber"
                          label="Celular"
                          required
                          {...field}
                          error={fieldState.error?.message}
                        />
                      </>
                    )}
                  />
                </div>
                <div>
                  <Input
                    type="password"
                    register={register}
                    name="customer.password"
                    label="Senha"
                    autoComplete="off"
                  />
                  <small className="text-xs text-gray-500">
                    Se vazio, usaremos a senha padrão: 123456
                  </small>
                </div>
              </FormRow>
            </SectionCard>
            <div className="flex justify-center gap-2">
              <Button
                onClick={() => setActiveStep(1)}
                color="gray"
                className="md:max-w-60"
              >
                Voltar
              </Button>
              <Button type="submit" className="md:max-w-60">
                Próximo
              </Button>
            </div>
          </form>
        )}

        {/* STEP 3 */}
        {activeStep === 3 && (
          <form
            onSubmit={handleSubmit(handleFinishSubmit)}
            className="space-y-6"
          >
            <SectionCard title="Revisão — revendedor">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {isCPF ? (
                  <ReviewItem label="Nome" value={watch("company.name")} />
                ) : (
                  <ReviewItem
                    label="Razão social"
                    value={watch("company.socialReason")}
                  />
                )}
                <ReviewItem label="E-mail" value={watch("company.email")} />
                <ReviewItem
                  label="Celular"
                  value={watch("company.phoneNumber")}
                />
                {!isCPF && (
                  <>
                    <ReviewItem
                      label="Responsável"
                      value={watch("company.responsibleName")}
                    />
                    <ReviewItem
                      label="CPF do responsável"
                      value={watch("company.responsibleCPF")}
                    />
                  </>
                )}
                <ReviewItem label="CEP" value={watch("company.cep")} />
                <ReviewItem label="Endereço" value={watch("company.address")} />
                <ReviewItem
                  label="Número"
                  value={watch("company.addressNumber")}
                />
                <ReviewItem
                  label="Complemento"
                  value={watch("company.addressComplement")}
                />
                <ReviewItem
                  label="Bairro"
                  value={watch("company.addressNeighborhood")}
                />
                <ReviewItem
                  label="Cidade"
                  value={watch("company.addressCity")}
                />
                {powerUser && (
                  <>
                    <ReviewItem
                      label="URL White-label"
                      value={watch("representative.representativeUrl")}
                    />
                    <ReviewItem
                      label="Token Asaas"
                      value={watch("representative.accessToken")}
                    />
                  </>
                )}
              </div>
            </SectionCard>

            <SectionCard title="Revisão — usuário">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ReviewItem label="Nome" value={watch("customer.name")} />
                <ReviewItem label="E-mail" value={watch("customer.email")} />
                <ReviewItem
                  label="Celular"
                  value={watch("customer.phoneNumber")}
                />
              </div>
            </SectionCard>
            <div className="flex justify-center gap-2">
              <Button
                onClick={() => setActiveStep(2)}
                color="gray"
                className="md:max-w-60"
              >
                Voltar
              </Button>
              <Button
                type="submit"
                className="md:max-w-60"
                isProcessing={isSubmitting}
              >
                Finalizar
              </Button>
            </div>

            {errorQuery && (
              <Alert color="failure" className="mt-3">
                Erro ao tentar cadastrar o revendedor!
              </Alert>
            )}
          </form>
        )}

        {/* STEP 4 */}
        {activeStep === 4 && (
          <div className="relative overflow-hidden rounded-2xl border bg-white shadow-md">
            {/* fundo com gradiente suave */}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-emerald-50 via-white to-emerald-100 opacity-70" />

            {/* confettis decorativos */}
            <svg
              className="pointer-events-none absolute -top-6 -left-6 w-28 h-28 opacity-50"
              viewBox="0 0 100 100"
              fill="none"
            >
              <circle cx="20" cy="20" r="3" fill="#10B981" />
              <rect x="60" y="10" width="6" height="6" rx="1" fill="#34D399" />
              <circle cx="85" cy="30" r="2.5" fill="#A7F3D0" />
              <rect x="12" y="70" width="5" height="5" rx="1" fill="#6EE7B7" />
              <circle cx="42" cy="85" r="2" fill="#10B981" />
            </svg>
            <svg
              className="pointer-events-none absolute -bottom-6 -right-6 w-28 h-28 opacity-50"
              viewBox="0 0 100 100"
              fill="none"
            >
              <circle cx="80" cy="80" r="3" fill="#10B981" />
              <rect x="30" y="84" width="6" height="6" rx="1" fill="#34D399" />
              <circle cx="18" cy="62" r="2.5" fill="#A7F3D0" />
              <rect x="82" y="22" width="5" height="5" rx="1" fill="#6EE7B7" />
              <circle cx="58" cy="12" r="2" fill="#10B981" />
            </svg>

            <div className="relative p-8 flex flex-col items-center text-center">
              {/* ícone grande em destaque */}
              <div className="mb-4 inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-100">
                <FaCheckCircle className="w-12 h-12 text-emerald-600" />
              </div>

              <h3 className="text-2xl font-bold text-gray-900">
                Representante cadastrado com sucesso!
              </h3>
              <p className="text-sm text-gray-600 mt-2 max-w-xl">
                Tudo certo por aqui. Você já pode gerenciar permissões, convidar
                usuários ou cadastrar outro representante.
              </p>

              {/* resumo rápido */}
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-2xl text-left">
                <div className="rounded-xl border bg-white/70 p-4">
                  <div className="text-xs uppercase tracking-wide text-gray-500">
                    Representante
                  </div>
                  <div className="font-medium text-gray-900">
                    {watch("company.typeCustomer") === "CPF"
                      ? watch("company.name")
                      : watch("company.socialReason")}
                  </div>
                </div>
                <div className="rounded-xl border bg-white/70 p-4">
                  <div className="text-xs uppercase tracking-wide text-gray-500">
                    E-mail
                  </div>
                  <div className="font-medium text-gray-900 break-words">
                    {watch("company.email")}
                  </div>
                </div>
                <div className="rounded-xl border bg-white/70 p-4">
                  <div className="text-xs uppercase tracking-wide text-gray-500">
                    Celular
                  </div>
                  <div className="font-medium text-gray-900">
                    {watch("company.phoneNumber")}
                  </div>
                </div>
                <div className="rounded-xl border bg-white/70 p-4">
                  <div className="text-xs uppercase tracking-wide text-gray-500">
                    Tipo de pagamento
                  </div>
                  <div className="font-medium text-gray-900">
                    {watch("company.typePayment")}
                  </div>
                </div>
              </div>

              {/* ações */}
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button
                  className="md:max-w-60"
                  onClick={() => {
                    reset(undefined, { keepValues: false });
                    setActiveStep(1);
                  }}
                >
                  Cadastrar outro
                </Button>
                <Button
                  color="light"
                  className="md:max-w-60"
                  onClick={() => {
                    navigate("/admin/representatives");
                  }}
                >
                  Ir para a lista
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminRepresentativeForm;
